/**
 * @Author  : zhouming
 * @Date    : 2025-06-12 10:34:12
 * c 端页面
 * 去掉 isPreview 参数,本地功能已经下线
 *
 * 存储以下全局属性:
 * 是否是移动端
 * 激活的 tab 的 key + 报表id
 * 全局筛选器数据
 *
 * 飞书文档地址:
 * https://open.feishu.cn/document/client-docs/gadget/-web-app-api/navigation/navigateto
 * https://open.feishu.cn/document/develop-web-apps/web-app-open-ability/hide-the-navigation-bar-button
 */
import type MobileTabsComponent from './preview-components/mobile-tabs';
import type PCTabsComponent from './preview-components/pc-tabs';
import type {TypeResponseSchema} from '@/types/common';

import {injectComponents} from '@alilc/lowcode-plugin-inject';
import ReactRenderer from '@alilc/lowcode-react-renderer';
import {
    buildComponents,
    assetBundle,
    AssetLevel,
    AssetLoader,
    AssetType,
    assetItem,
    AssetBundle,
} from '@alilc/lowcode-utils';
import {InfoCircleFilled} from '@ant-design/icons';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import React, {useEffect, useState, useRef, lazy, Suspense} from 'react';
import {createRoot} from 'react-dom/client';
import toast, {Toaster} from 'react-hot-toast';

import '@/util/sentryInit';

import {
    Loading,
    Empty,
    isFeishuApp,
    checkComponentPermission,
    addCustomPublicFilterRuleList,
    getURLParams,
    getProcessEnv,
    isAssetLoaded,
    areAllAssetsLoaded,
    TabsLoadingSkeleton,
    extractUrlsFromAssetBundle,
} from './preview-components';

import {serveGetSchema} from '@/preview-components/utils';

import './preview.scss';

declare global {
    interface Window {
        __bi__equipment__: 'mobile' | 'pc';
        __bi__tabs__key__?: (string | undefined)[];
        __bi__dashboard__id__?: string;
        // 全局筛选器传参功能的数据
        __bi_global_filter_value__?: Array<{showName: string; filterValue: string[]}>;
    }
}

type TabPane = NonNullable<TypeResponseSchema['packageList']>[0];

// 自定义飞书导航栏 - 已注释，使用飞书原生导航栏
// const FeishuNavBar = lazy(
//     () =>
//         import(
//             /* webpackChunkName: "feishu-navbar" */
//             /* webpackMode: "lazy" */
//             './preview-components/feishu-navbar'
//         ),
// );

const MobileTabs = lazy(
    () =>
        import(
            /* webpackChunkName: "mobile-tabs" */
            /* webpackMode: "lazy" */
            './preview-components/mobile-tabs'
        ),
);

const PCTabs = lazy(
    () =>
        import(
            /* webpackChunkName: "pc-tabs" */
            /* webpackMode: "lazy" */
            './preview-components/pc-tabs'
        ),
);

// 类型提取：从组件 Props 中提取 renderContent 的参数类型
type PCRenderContentParams = Parameters<React.ComponentProps<typeof PCTabsComponent>['renderContent']>[0];
type MobileRenderContentParams = Parameters<React.ComponentProps<typeof MobileTabsComponent>['renderContent']>[0];

const ENV = getProcessEnv();

// PC 和移动端标识
const SYMBOL_EQUIPMENT = '__bi__equipment__';
// 激活的 tab 的 key
const SYMBOL_TABS_KEY = '__bi__tabs__key__';
// 选择的报表标志
const SYMBOL_DASHBOARD_ID = '__bi__dashboard__id__';

const SYMBOL_GLOBAL_FILTER_VALUE = '__bi_global_filter_value__';

const libraryMap = {
    // package: library 的规则
    'taitan-components-pc': 'TaitanComponentPC',
};

/**
 * @description: 加载展示页需要的 js、css 资源
 * 只加载组件库
 * 其他包在 html 中加载而不是此处
 */
const loadCustomPackage = async (materialVersion: string) => {
    const isTestEnv = ENV === 'test';
    const cacheBuster = isTestEnv ? Date.now() : '';
    const cacheParam = cacheBuster ? `?t=${cacheBuster}` : '';

    const jsUrl = `//gh-fe.oss-cn-beijing.aliyuncs.com/dis/component/taitan-components-pc/${materialVersion}/${ENV}/render/default/view.js${cacheParam}`;
    const cssUrl = `//gh-fe.oss-cn-beijing.aliyuncs.com/dis/component/taitan-components-pc/${materialVersion}/${ENV}/render/default/view.css${cacheParam}`;

    // 创建 AssetBundle 对象
    const libraryAsset = assetBundle(
        [
            assetItem(
                AssetType.JSUrl,
                jsUrl,
                AssetLevel.Library,
                `taitan-components-pc-${materialVersion}-js${cacheBuster ? `-${cacheBuster}` : ''}`,
            ),
            assetItem(
                AssetType.CSSUrl,
                cssUrl,
                AssetLevel.Library,
                `taitan-components-pc-${materialVersion}-css${cacheBuster ? `-${cacheBuster}` : ''}`,
            ),
        ],
        AssetLevel.Library,
    );
    // 检查 AssetBundle 对象是否创建成功
    if (!libraryAsset) {
        console.error('❌ 创建 AssetBundle 对象失败');
        return;
    }

    // 从 AssetBundle 对象中提取 URL 数组用于检查
    // const assetUrls = extractUrlsFromAssetBundle(libraryAsset);

    // 🚀 优化：检查资源是否已经加载完成，避免重复加载
    // if (areAllAssetsLoaded(assetUrls)) {
    //     console.log('📦 资源已加载完成，跳过重复加载:', assetUrls);
    //     return;
    // }

    // console.log('📦 开始加载资源:', assetUrls);
    const assetLoader = new AssetLoader();

    // ❗❗❗ 本步骤很耗时 加载资源时间 + 200-400ms
    await assetLoader.load(libraryAsset);
    // console.log('✅ 资源加载完成:', assetUrls);
};

/**
 * 获得适当的 schema 信息和包信息
 * 默认从 url 上读取 dashboardId 和 homeId
 * 如果传入这两个 id 则优先使用传入的 id
 */
const getSchemaAndPackages = async (params?: {dashboardId?: string; homeId?: string}) => {
    const {
        // 报表 id
        dashboardId,
        // 门户 id
        homeId,
        appId,
        source,
        cosplayCasAuthorization,
        versionId,
    } = getURLParams();

    let packages;
    let projectSchema;

    // 请求头
    const requestConfig = {headers: {appId, source, ['cosplay-cas-authorization']: cosplayCasAuthorization}};

    // 本步骤实际耗时大概是 150ms + 接口
    const res = await serveGetSchema(
        {
            homeId: params?.homeId || homeId,
            dashboardId: params?.dashboardId || dashboardId,
            // 区分是否配置端
            isConfig: false,
            versionId,
        },
        requestConfig,
    );

    const {data} = res;
    const {
        jsPackages,
        dashboardName,
        dashboardHtmlJson,
        unitBlackPermissions = [],
        packageVersion,
        packageList,
        dashboardLevel,
        clientInfo,
        homeName,
    } = data || {};

    try {
        packages = JSON.parse(jsPackages);
        projectSchema = checkComponentPermission(JSON.parse(dashboardHtmlJson), unitBlackPermissions);

        const customPublicFilterRuleList = data?.customHtmlFormat?.customPublicFilterRuleList;

        // 如果 customPublicFilterRuleList 不为空，则添加到 schema 的根组件 props 中
        if (customPublicFilterRuleList && customPublicFilterRuleList.length > 0) {
            projectSchema = addCustomPublicFilterRuleList(projectSchema, customPublicFilterRuleList);
        }
        // console.log('projectSchema: ', projectSchema);
    } catch (error) {
        console.log('error: ', error);
        toast.error('数据序列化失败请检查数据格式');
    }

    // 当获得 dashboardLevel 时，更新 sessionStorage 中的 __BI_SOURCE__
    if (dashboardLevel !== undefined) {
        const currentBiSource = sessionStorage.getItem('__BI_SOURCE__');

        if (currentBiSource) {
            try {
                const biSourceData = JSON.parse(currentBiSource);
                const updatedBiSource = {
                    ...biSourceData,
                    headers: {
                        ...biSourceData.headers,
                        dashboardLevel,
                    },
                };
                sessionStorage.setItem('__BI_SOURCE__', JSON.stringify(updatedBiSource));
            } catch (error) {
                console.error('更新 __BI_SOURCE__ 失败:', error);
            }
        }
    }

    return {
        packages,
        projectSchema,
        materialVersion: packageVersion,
        pageTitle: homeName || dashboardName,
        packageList,
        clientInfo,
    };
};

/**
 * 初始化 React 组件
 */
const initReactComponents = async (componentsMapArray: any) => {
    const componentsMap: any = {};

    componentsMapArray.forEach((component: any) => {
        componentsMap[component.componentName] = component;
    });

    const components = await injectComponents(
        // @ts-ignore
        buildComponents(
            libraryMap,
            componentsMap,
            // 如果是 "Page" 则不会执行这个函数 只在是 isComponentSchema || isLowcodeProjectSchema 时候执行
            // createComponent
        ),
    );

    return components;
};

/**
 * 获得数据
 */
async function init(setData: (data: any) => void) {
    const {
        projectSchema,
        materialVersion,
        pageTitle,
        packageList,
        clientInfo = {clientType: 'pc'},
    } = await getSchemaAndPackages();

    const {componentsMap, config, componentsTree, i18n, dataSource: projectDataSource} = projectSchema;

    if (!componentsMap || !componentsTree) {
        toast.error('缺少数据');
    }

    await loadCustomPackage(materialVersion);

    const components = await initReactComponents(componentsMap);

    const res = {
        // 第一项是 page 这个数组只有一项 直接传入会报错 结构异常
        schema: componentsTree[0],
        components,
        i18n,
        projectDataSource,
        config,
        pageTitle,
        packageList,
        clientInfo,
    };

    setData(res);

    return res;
}

/**
 * 报表主体内容渲染
 * 当 id 变化且传入的 initSchema 为空时，重新加载数据
 */
const ReportContent = ({
    id,
    initSchema,
    initComponents,
    initConfig,
}: {
    id?: string;
    initSchema?: any;
    initComponents: any;
    initConfig: any;
}) => {
    const {appId, source} = getURLParams();

    // 存储选择的报表标志
    window[SYMBOL_DASHBOARD_ID] = id;

    const [data, setData] = useState(() => {
        return {
            // 空字符串表示是没配置 undefined 表示是正在加载
            schema: initSchema,
            components: initComponents,
            config: initConfig,
        };
    });

    const {schema, config, components} = data as {
        schema: any;
        config: any;
        components: any;
    };

    useEffect(() => {
        return () => {
            window[SYMBOL_DASHBOARD_ID] = undefined;
        };
    }, []);
    /**
     * 当 id 变化且传入的 initSchema 为空时，重新加载数据
     * 同时当卸载的时候清除 window 上 id 的值
     */
    useEffect(() => {
        if (schema) {
            return;
        }

        if (id) {
            getSchemaAndPackages({dashboardId: id})
                .then(async ({projectSchema, pageTitle}) => {
                    // 旧响应，直接丢弃
                    // if (current !== reqMark) {
                    //     console.log('旧响应，直接丢弃');
                    //     return;
                    // }

                    const {config, componentsTree, dataSource: projectDataSource, componentsMap} = projectSchema;

                    const components = await initReactComponents(componentsMap);

                    return setData((pre) => ({
                        ...pre,
                        schema: componentsTree[0],
                        projectDataSource,
                        config,
                        pageTitle,
                        components,
                    }));
                })
                .catch((error) => {
                    console.log('error', error);
                });
        }
        // 没有报表 id 说明没配置报表
        else {
            setData((pre) => ({
                ...pre,
                schema: '',
            }));
        }
    }, [id, schema]);

    // 非门户状态：检查是否有内容可显示
    if (schema === undefined || !components) {
        return <Loading style={{margin: '50px auto'}} />;
    }

    // 检查是否为空内容
    if (schema === '' || schema?.children?.[0]?.props?.layout?.length === 0) {
        return <Empty />;
    }

    // const clientType = config?.clientType;

    // 之前的画布 375px时为移动端页面，且手动设置成居中显示，
    // 本次新的移动端画布通过config中clientType来控制，如果不带有此属性则看作历史报表，
    // 带有此属性看作诗新的报表，既可以兼容历史报表，也可以兼容新报表
    // const isMobile = clientType === 'mobile';

    return (
        <ReactRenderer
            className="bi-react-renderer"
            schema={{...schema}}
            components={components}
            // messages={i18n}
            appHelper={{constants: {appId, source}}}
            style={{
                height: 'auto',
            }}
        />
    );
};

// --------------------------------------------------------------------------------------------------------------
// ------------------------------------------------ 主代码开始 ----------------------------------------------------
// --------------------------------------------------------------------------------------------------------------

/**
 * 主函数
 * 主要承担第一次的数据请求
 */
const Preview = () => {
    // console.log('SamplePreview 渲染次数');

    const {
        cosplayCasAuthorization,
        cnName,
        // 这两个字段是上报作用
        appId,
        source,
        dashboardId,
        tabActiveKeyList,
    } = getURLParams();

    const [data, setData] = useState({});

    // 存储激活的 tab 的 key 值是 [一级tab 的 id, 二级tab 的 id]
    const [activeKey, setActiveKey] = useState<Array<string>>(() => {
        // 从 url 中完成初始化
        if (tabActiveKeyList) {
            return tabActiveKeyList.split(',').map((item) => item.trim());
        }

        return [];
    });

    const containerRef = useRef<HTMLDivElement | null>(null);
    // 是否是第一次加载 如果切换 tab 则需要重新加载
    const firstLoadRef = useRef(true);

    useState(() => {
        // 存储全局数据
        const {appId, source, versionId, cosplayCasAuthorization} = getURLParams();
        sessionStorage.setItem(
            '__BI_SOURCE__',
            JSON.stringify({headers: {appId, source, 'cosplay-cas-authorization': cosplayCasAuthorization}, versionId}),
        );
    });

    // 表示处于模拟用户状态
    const inMock = cosplayCasAuthorization && cnName;
    const {
        schema: initSchema,
        config: initConfig,
        components: initComponents,
        packageList,
        pageTitle,
        clientInfo,
    } = data as {
        schema: any;
        config: any;
        components: any;
        packageList: TypeResponseSchema['packageList'];
        pageTitle: string;
        clientInfo: NonNullable<TypeResponseSchema['clientInfo']>;
    };

    // 报表或者门户的容器类型,不会发生变化
    const mainType = clientInfo?.clientType;

    // 是否是门户状态
    const isHomeStatus = packageList && packageList.length > 0;

    /**
     * 点击一级标题
     * 注：当前使用飞书原生导航栏，此函数暂未使用，但保留以便后续切换回自定义导航栏
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleParentClick = (activeKey: string) => {
        firstLoadRef.current = false;

        const activeTab = (packageList || []).find((tab) => tab.id === activeKey) as TabPane;

        if (activeTab?.childrenList?.length) {
            // 一级带子集 → 自动激活第一个二级
            setActiveKey([activeTab.id, activeTab.childrenList[0].id]);
        } else {
            setActiveKey([activeTab.id, '']);
        }
    };

    /**
     * 监听页面大小变化
     * 设置页面标题
     */
    useEffect(() => {
        // 设置页面标题
        if (pageTitle) {
            document.title = pageTitle;
        }

        if (!containerRef.current || window.top === window.self) {
            return;
        }

        const callback = debounce(() => {
            const clientHeight = containerRef.current?.clientHeight;
            window.parent.postMessage(
                {
                    name: 'bigDataPageResize',
                    data: {
                        clientHeight,
                    },
                },
                '*',
            );
        }, 300);

        const observer = new MutationObserver(callback);
        observer.observe(containerRef.current, {attributes: true, childList: true, subtree: true});

        return () => {
            observer.disconnect();
        };
    }, [data]);

    useEffect(() => {
        // 初始化数据
        init(setData)
            .then((res) => {
                const {packageList = []} = res;

                // 如果激活的 tab 的 key 为空，则设置为第一项
                if (activeKey.length === 0 && packageList.length > 0) {
                    setActiveKey([packageList[0].id, packageList[0].childrenList?.[0]?.id || '']);
                }

                return res;
            })
            .catch((error) => {
                toast.error(`页面初始化失败,原因: ${error.message}`);
            });

        // 当前页面关闭清除全局筛选器传参功能的数据
        return () => {
            window.__bi_global_filter_value__ = undefined;
        };
    }, []);

    /**
     * 存储激活的 tab 的 key
     */
    useEffect(() => {
        if (isHomeStatus) {
            window[SYMBOL_TABS_KEY] = activeKey;
        }

        return () => {
            window[SYMBOL_TABS_KEY] = undefined;
        };
    }, [activeKey, isHomeStatus]);

    // 初次的加载状态
    if (initSchema === undefined || !initComponents) {
        return <Loading style={{margin: '100px auto'}} />;
    }

    const isMobile = mainType === 'mobile';

    // 注：当前使用飞书原生导航栏，此变量暂未使用，但保留以便后续切换回自定义导航栏
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const inFeishu = isFeishuApp();

    return (
        <div
            className={classNames('lowcode-plugin-sample-preview', {
                // 使用飞书原生导航栏时，不需要添加自定义导航栏的样式类
                // 'feishu-custom-navbar': isFeishuApp(),
                'preview-mobile': isMobile,
            })}
            ref={containerRef}
            data-env={ENV}
            data-appid={appId}
            data-source={source}
            id={dashboardId}
        >
            {/* 飞书自定义导航栏 - 已注释，使用飞书原生导航栏 */}
            {/* {inFeishu && (
                <Suspense
                    fallback={
                        <div
                            style={{
                                height: 'var(--feishu-navbar-height, 44px)',
                                backgroundColor: '#fff',
                                borderBottom: '1px solid #f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                color: '#999',
                            }}
                        >
                            加载中...
                        </div>
                    }
                >
                    <FeishuNavBar
                        title={pageTitle}
                        tabItems={packageList || []}
                        activeTabKey={activeKey[0]}
                        onTabChange={handleParentClick}
                    />
                </Suspense>
            )} */}

            {inMock && (
                <div className="preview-header">
                    <InfoCircleFilled
                        style={{
                            marginRight: 4,
                            color: '#1890ff',
                        }}
                    />
                    你正在模拟&quot;
                    <strong>{decodeURIComponent(cnName)}</strong>
                    &quot;的身份在预览页面验证权限
                </div>
            )}

            <div
                className={classNames('preview-content', {
                    'preview-content-mock': inMock,
                    'preview-content-mobile': isMobile,
                })}
                data-habo-fmp-hero
            >
                {/* 门户状态 - 根据客户端类型选择不同的Tab组件 */}
                {isHomeStatus ? (
                    isMobile ? (
                        // 移动端 - 动态加载
                        <Suspense fallback={<TabsLoadingSkeleton type="mobile" />}>
                            <MobileTabs
                                packageList={packageList || []}
                                activeKey={activeKey}
                                setActiveKey={setActiveKey}
                                setFirstLoadRefFalse={() => (firstLoadRef.current = false)}
                                renderContent={({
                                    currentOpenId,
                                    hasChildren,
                                    isActive,
                                    clientInfo,
                                }: MobileRenderContentParams) => {
                                    // const isClientTypeMatch = (clientInfo?.clientType ?? 'pc') === mainType;

                                    // if (!isClientTypeMatch) {
                                    //     return <Empty />;
                                    // }

                                    if (hasChildren) {
                                        return (
                                            <ReportContent
                                                id={currentOpenId}
                                                key={currentOpenId}
                                                initComponents={initComponents}
                                                initSchema={firstLoadRef.current ? initSchema : undefined}
                                                initConfig={initConfig}
                                            />
                                        );
                                    }

                                    return (
                                        <ReportContent
                                            id={currentOpenId}
                                            initComponents={initComponents}
                                            initSchema={firstLoadRef.current ? initSchema : undefined}
                                            initConfig={initConfig}
                                        />
                                    );
                                }}
                            />
                        </Suspense>
                    ) : (
                        // PC 端 - 动态加载
                        <Suspense fallback={<TabsLoadingSkeleton type="pc" />}>
                            <PCTabs
                                packageList={packageList || []}
                                activeKey={activeKey}
                                setActiveKey={setActiveKey}
                                setFirstLoadRefFalse={() => (firstLoadRef.current = false)}
                                renderContent={({
                                    currentOpenId,
                                    clientInfo,
                                    hasChildren,
                                    isActive,
                                }: PCRenderContentParams) => {
                                    // const isClientTypeMatch = (clientInfo?.clientType ?? 'pc') === mainType;

                                    // if (!isClientTypeMatch) {
                                    //     return <Empty />;
                                    // }

                                    return (
                                        <ReportContent
                                            id={currentOpenId}
                                            // key={hasChildren ? currentOpenId : undefined}
                                            initComponents={initComponents}
                                            initSchema={firstLoadRef.current ? initSchema : undefined}
                                            initConfig={initConfig}
                                        />
                                    );
                                }}
                            />
                        </Suspense>
                    )
                ) : (
                    // 非门户状态 直接渲染报表
                    <div className="preview-report-content-container">
                        <ReportContent
                            id={dashboardId}
                            initComponents={initComponents}
                            initSchema={initSchema}
                            initConfig={initConfig}
                        />
                    </div>
                )}
            </div>
            <Toaster
                position="top-center"
                reverseOrder={false}
                gutter={8}
                containerClassName=""
                containerStyle={{
                    // Toaster 位置调整：避免在手机端被前置摄像头遮挡
                    top: 'env(safe-area-inset-top, 20px)', // 使用安全区域，默认 20px
                    marginTop: '20px', // 额外增加上边距
                }}
            />
        </div>
    );
};

(window.habo as any)?.fmp?.init?.();

const container = document.getElementById('preview-container');
const root = createRoot(container!);

root.render(<Preview />);
